import type React from 'react'

import { type MarkerProps } from '@react-google-maps/api'

import { CompletedRequestDestinationMarker } from '@/components/organisms/Map/Markers/CompletedRequestDestinationMarker.tsx'

import { PendingRequestMarker } from './PendingRequestMarker.tsx'
import { PinRequestMarker } from './PinRequestMarker.tsx'
import { RequestDestinationMarker } from './RequestDestinationMarker.tsx'
import { TeamMarker } from './TeamMarker.tsx'

export type TransportRequestMarkerProps = {
  id: string
  status:
    | 'pending'
    | 'assigned'
    | 'allocated'
    | 'onboarded'
    | 'completed'
    | 'team-assigned'
    | 'team-allocated'
    | 'team-on-board'
    | 'pending-origin'
    | undefined
  locationLabel: string
  origin: google.maps.LatLngLiteral
  destination: google.maps.LatLngLiteral
  team?: {
    id: string
    carNumber: string
    carType?: 'automatic' | 'manual' | 'electric'
    vip?: boolean
  }
  plannedTeam?: {
    id: string
    carNumber: string
    endPosition: google.maps.LatLngLiteral
    carType?: 'automatic' | 'manual' | 'electric'
    vip?: boolean
  }
  selected?: boolean
  children?: React.ReactNode
} & Omit<MarkerProps, 'position' | 'icon' | 'label'>

export const TransportRequestMarker: React.FC<TransportRequestMarkerProps> = ({
  id,
  status,
  locationLabel,
  origin,
  destination,
  team,
  selected = false,
  visible,
  plannedTeam,
  ...props
}) => {
  switch (status) {
    case 'pending':
      return (
        <>
          <PendingRequestMarker {...props} position={origin} label={locationLabel} visible={visible} />
          <RequestDestinationMarker
            {...props}
            position={destination}
            label={locationLabel}
            visible={selected && visible}
          />
          {plannedTeam && (
            <TeamMarker
              {...plannedTeam}
              key={`${id}-${plannedTeam.id}-flagged`}
              teamId={plannedTeam.id}
              position={plannedTeam.endPosition}
              status="onboardFlagged"
              flagged
              visible={visible}
            />
          )}
        </>
      )
    case 'pending-origin':
      return <PendingRequestMarker {...props} position={origin} label={locationLabel} visible={visible} />
    case 'assigned':
    case 'allocated':
      return (
        <>
          <PinRequestMarker type={status} {...props} position={origin} label={locationLabel} visible={visible} />
          <RequestDestinationMarker
            {...props}
            position={destination}
            label={locationLabel}
            visible={selected && visible}
          />
          {team && (
            <TeamMarker
              key={`${id}-${team.id}-${status}`}
              teamId={team.id}
              status={status}
              {...team}
              visible={visible}
            />
          )}
        </>
      )
    case 'onboarded':
      return (
        <>
          <PinRequestMarker
            type="allocated"
            {...props}
            position={origin}
            label={locationLabel}
            visible={selected && visible}
          />
          <RequestDestinationMarker {...props} position={destination} label={locationLabel} visible={visible} />
          {team && (
            <TeamMarker
              key={`${id}-${team.id}-${status}`}
              teamId={team.id}
              status={status}
              {...team}
              visible={visible}
            />
          )}
        </>
      )
    case 'team-on-board':
      return <RequestDestinationMarker {...props} position={destination} label={locationLabel} visible={visible} />
    case 'team-assigned':
      return (
        <>
          <PinRequestMarker type="assigned" {...props} position={origin} label={locationLabel} visible={visible} />
          <RequestDestinationMarker {...props} position={destination} label={locationLabel} visible={visible} />
        </>
      )
    case 'team-allocated':
      return (
        <>
          <PinRequestMarker type="allocated" {...props} position={origin} label={locationLabel} visible={visible} />
          <RequestDestinationMarker {...props} position={destination} label={locationLabel} visible={visible} />
        </>
      )
    case 'completed':
      return (
        <>
          <PinRequestMarker type="allocated" {...props} position={origin} label="" visible={selected && visible} />
          <CompletedRequestDestinationMarker {...props} position={destination} visible={visible} />
        </>
      )
    default:
      return null
  }
}
